import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toast, useToastManager, toast } from "./components/Toast";
import { SyncStatus } from "./components/SyncStatus";
import { IdeaList } from "./components/IdeaList";
import { RefineView } from "./components/RefineView";
import { Settings } from "./components/Settings";
import { ConnectDiveView } from "./components/ConnectDiveView";
import { getDiveConnection, clearDiveConnection, initiateDiveOAuth, flushPendingDiveTasks, type DiveConnection } from "./lib/dive";
import {
  getActiveIdeas,
  getActiveProjects,
  getActiveRepos,
  getMessagesForIdea,
  upsertIdea,
  upsertMessage,
  upsertProject,
  upsertRepo,
  softDeleteIdea,
  softDeleteProject,
  softDeleteRepo,
} from "./lib/db";
import { initSyncListeners, scheduleSyncDebounced, setSyncStatusCallback, sync } from "./lib/sync";
import { handleCallback, initiateLogin, isAuthenticated } from "./lib/auth";
import { fetchMe } from "./lib/api";
import type { Idea, Message, Project, Repo, SyncStatus as SyncStatusType } from "./lib/types";
import { generateId } from "./lib/types";

type Tab = "ideas" | "refine" | "settings";

function AutoConnectView({ diveUrl }: { diveUrl: string }) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    initiateDiveOAuth(diveUrl).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to start connection");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 px-6 text-center">
        <p className="text-sm font-mono text-red-500">{error}</p>
        <button onClick={() => window.close()} className="text-sm font-mono text-gray-400 hover:text-gray-700">
          Close tab
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen font-mono text-sm text-gray-400">
      Connecting to Dive…
    </div>
  );
}

function CallbackHandler({ onDone }: { onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    handleCallback(params)
      .then(() => {
        window.history.replaceState({}, "", "/");
        onDone();
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Auth failed");
      });
  }, [onDone]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen font-mono text-sm text-red-500 px-4 text-center">
        Auth error: {error}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen font-mono text-sm text-gray-400">
      Completing sign in…
    </div>
  );
}

export default function App() {
  const { toasts, dismissToast } = useToastManager();
  const [tab, setTab] = useState<Tab>("ideas");
  const [selectedIdeaId, setSelectedIdeaId] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatusType>({ kind: "idle" });
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [messages, setMessages] = useState<Map<string, Message[]>>(new Map());
  const [projects, setProjects] = useState<Project[]>([]);
  const [repos, setRepos] = useState<Repo[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [userLogin, setUserLogin] = useState<string | null>(null);
  const [authing, setAuthing] = useState(false);
  const [diveConnection, setDiveConnection] = useState<DiveConnection | null>(() => getDiveConnection());
  const cleanupRef = useRef<(() => void) | null>(null);

  const isCallback = window.location.pathname === "/callback";
  const isConnect = window.location.pathname === "/connect";

  // Reload local data from IndexedDB
  const reloadLocal = useCallback(async (uid: string) => {
    const [ideasData, projectsData, reposData] = await Promise.all([
      getActiveIdeas(uid),
      getActiveProjects(uid),
      getActiveRepos(uid),
    ]);
    setIdeas(ideasData.sort((a, b) => b.updatedAt - a.updatedAt));
    setProjects(projectsData);
    setRepos(reposData);

    // Load messages for visible ideas
    const msgMap = new Map<string, Message[]>();
    await Promise.all(
      ideasData.map(async (idea) => {
        const msgs = await getMessagesForIdea(idea.id);
        msgMap.set(idea.id, msgs);
      }),
    );
    setMessages(msgMap);
  }, []);

  // Initialize auth + sync
  useEffect(() => {
    if (isCallback) return;

    async function init() {
      if (!isAuthenticated()) {
        return; // Will show sign-in prompt
      }
      try {
        setAuthing(true);
        const me = await fetchMe();
        setUserId(me.login);
        setUserLogin(me.login);
        await reloadLocal(me.login);

        setSyncStatusCallback((status) => {
          setSyncStatus(status);
          if (status.kind === "synced") {
            reloadLocal(me.login).catch(console.error);
          }
        });

        cleanupRef.current = initSyncListeners(me.login);
      } catch (err) {
        console.error("Init error:", err);
      } finally {
        setAuthing(false);
      }
    }

    init();
    return () => { cleanupRef.current?.(); };
  }, [isCallback, reloadLocal]);

  const handleManualSync = useCallback(() => {
    if (userId) sync(userId).catch(console.error);
  }, [userId]);

  // Keyboard: Esc exits refine view back to list (mobile)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || tab !== "refine") return;
      const t = e.target as HTMLElement;
      if (t.tagName === "INPUT" || t.tagName === "TEXTAREA") return;
      setTab("ideas");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [tab]);

  // Flush queued Dive tasks when coming back online
  useEffect(() => {
    if (!diveConnection) return;
    const flush = () => {
      flushPendingDiveTasks(diveConnection)
        .then((count) => {
          if (count > 0) toast({ kind: "success", message: `${count} queued task${count === 1 ? "" : "s"} sent to Dive` });
        })
        .catch(console.error);
    };
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, [diveConnection]);

  // CRUD operations

  const handleCreateIdea = useCallback(
    async (title: string, brainDump: string, projectId: string | null) => {
      if (!userId) return;
      const now = Date.now();
      const idea: Idea = {
        id: generateId(),
        userId,
        title,
        brainDump,
        status: "raw",
        projectId,
        preferredRepo: null,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      };
      await upsertIdea(idea);
      await reloadLocal(userId);
      scheduleSyncDebounced(userId);
      setSelectedIdeaId(idea.id);
      setTab("refine");
    },
    [userId, reloadLocal],
  );

  const handleDeleteIdea = useCallback(
    async (id: string) => {
      if (!userId) return;
      await softDeleteIdea(id);
      await reloadLocal(userId);
      scheduleSyncDebounced(userId);
      if (selectedIdeaId === id) {
        setSelectedIdeaId(null);
        setTab("ideas");
      }
    },
    [userId, reloadLocal, selectedIdeaId],
  );

  const handleUpdateIdea = useCallback(
    async (updates: Partial<Idea>) => {
      if (!userId || !selectedIdeaId) return;
      const existing = ideas.find((i) => i.id === selectedIdeaId);
      if (!existing) return;
      const updated = { ...existing, ...updates, updatedAt: Date.now() };
      await upsertIdea(updated);
      await reloadLocal(userId);
      scheduleSyncDebounced(userId);
    },
    [userId, selectedIdeaId, ideas, reloadLocal],
  );

  const handleAddMessage = useCallback(
    async (msg: Omit<Message, "id" | "updatedAt" | "createdAt" | "deletedAt">) => {
      if (!userId) return;
      const now = Date.now();
      const message: Message = {
        ...msg,
        id: generateId(),
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      };
      await upsertMessage(message);
      // Bump idea updatedAt; auto-advance raw → refining on first user message
      const idea = ideas.find((i) => i.id === msg.ideaId);
      if (idea) {
        const statusUpdate = idea.status === "raw" && msg.role === "user" ? { status: "refining" as const } : {};
        await upsertIdea({ ...idea, ...statusUpdate, updatedAt: now });
      }
      await reloadLocal(userId);
      scheduleSyncDebounced(userId);
    },
    [userId, ideas, reloadLocal],
  );

  const handleCreateProject = useCallback(
    async (project: Project) => {
      if (!userId) return;
      await upsertProject(project);
      await reloadLocal(userId);
      scheduleSyncDebounced(userId);
    },
    [userId, reloadLocal],
  );

  const handleUpdateProject = useCallback(
    async (project: Project) => {
      if (!userId) return;
      await upsertProject({ ...project, updatedAt: Date.now() });
      await reloadLocal(userId);
      scheduleSyncDebounced(userId);
    },
    [userId, reloadLocal],
  );

  const handleDeleteProject = useCallback(
    async (id: string) => {
      if (!userId) return;
      await softDeleteProject(id);
      await reloadLocal(userId);
      scheduleSyncDebounced(userId);
    },
    [userId, reloadLocal],
  );

  const handleAddRepo = useCallback(
    async (repo: Repo) => {
      if (!userId) return;
      await upsertRepo(repo);
      await reloadLocal(userId);
      scheduleSyncDebounced(userId);
    },
    [userId, reloadLocal],
  );

  const handleDeleteRepo = useCallback(
    async (slug: string) => {
      if (!userId) return;
      await softDeleteRepo(userId, slug);
      await reloadLocal(userId);
      scheduleSyncDebounced(userId);
    },
    [userId, reloadLocal],
  );

  const selectedIdea = useMemo(
    () => ideas.find((i) => i.id === selectedIdeaId) ?? null,
    [ideas, selectedIdeaId],
  );

  const selectedMessages = useMemo(
    () => (selectedIdeaId ? messages.get(selectedIdeaId) ?? [] : []),
    [messages, selectedIdeaId],
  );

  // --- Render ---

  if (isCallback) {
    return (
      <CallbackHandler
        onDone={() => {
          window.location.href = "/";
        }}
      />
    );
  }

  if (isConnect) {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    const diveUrl = params.get("dive_url");

    // Entry point from Dive Settings: dive_url present but no OAuth code yet
    if (diveUrl && !code && !error) {
      return <AutoConnectView diveUrl={diveUrl} />;
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-screen gap-4 px-6 text-center">
          <p className="text-sm font-mono text-red-500">
            {error === "access_denied" ? "Connection was denied." : `OAuth error: ${error}`}
          </p>
          <button onClick={() => window.close()} className="text-sm font-mono text-gray-400 hover:text-gray-700">
            Close tab
          </button>
        </div>
      );
    }

    if (!code || !state) {
      return (
        <div className="flex items-center justify-center h-screen font-mono text-sm text-red-500 px-4 text-center">
          Invalid callback — missing code or state.
        </div>
      );
    }

    return <ConnectDiveView code={code} state={state} />;
  }

  if (authing) {
    return (
      <div className="flex items-center justify-center h-screen font-mono text-sm text-gray-400">
        Loading…
      </div>
    );
  }

  if (!isAuthenticated()) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-6 px-6">
        <h1 className="font-syne text-4xl font-bold text-gray-900">Crux</h1>
        <p className="text-sm font-mono text-gray-500 text-center max-w-xs">
          Personal idea scratchpad. AI refinement via Claude — no API key needed.
        </p>
        <button
          onClick={() => initiateLogin().catch(console.error)}
          className="bg-gray-900 text-white font-mono font-bold text-sm px-8 py-3 rounded-xl hover:bg-gray-700 transition-colors"
        >
          Sign in with GitHub
        </button>
      </div>
    );
  }

  const ideaList = (
    <IdeaList
      ideas={ideas}
      messages={messages}
      projects={projects}
      userId={userId ?? ""}
      selectedIdeaId={selectedIdeaId}
      onSelectIdea={(id) => { setSelectedIdeaId(id); setTab("refine"); }}
      onDeleteIdea={handleDeleteIdea}
      onCreateIdea={handleCreateIdea}
      onCreateProject={handleCreateProject}
    />
  );

  const refinePanel = selectedIdea ? (
    <div className="h-full max-w-2xl mx-auto w-full flex flex-col">
      <RefineView
        idea={selectedIdea}
        messages={selectedMessages}
        projects={projects}
        repos={repos}
        diveConnection={diveConnection}
        onUpdateIdea={handleUpdateIdea}
        onAddMessage={handleAddMessage}
      />
    </div>
  ) : (
    <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-300">
      <span className="font-syne text-5xl font-bold tracking-tight">◆</span>
      <p className="text-sm font-mono">Select an idea to refine</p>
    </div>
  );

  const settingsPanel = userId ? (
    <Settings
      login={userLogin}
      projects={projects}
      repos={repos}
      userId={userId}
      diveConnection={diveConnection}
      onUpdateProject={handleUpdateProject}
      onDeleteProject={handleDeleteProject}
      onAddRepo={handleAddRepo}
      onDeleteRepo={handleDeleteRepo}
      onCreateProject={handleCreateProject}
      onDisconnectDive={() => { clearDiveConnection(); setDiveConnection(null); }}
    />
  ) : null;

  return (
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
      <Toast toasts={toasts} onDismiss={dismissToast} />

      {/* Header */}
      <header className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-100 shrink-0">
        <h1 className="font-syne text-xl font-bold text-gray-900">Crux</h1>
        <div className="flex items-center gap-5">
          <SyncStatus status={syncStatus} onSync={handleManualSync} />
          <button
            onClick={() => setTab(tab === "settings" ? (selectedIdea ? "refine" : "ideas") : "settings")}
            className={`hidden md:block text-sm font-mono transition-colors ${
              tab === "settings" ? "text-gray-900 font-bold" : "text-gray-400 hover:text-gray-700"
            }`}
          >
            Settings
          </button>
          <button
            onClick={() => setTab("settings")}
            className="md:hidden text-sm text-gray-400 hover:text-gray-700 transition-colors"
          >
            ⚙
          </button>
        </div>
      </header>

      {/* Desktop: two-column layout, max width centered */}
      <div className="hidden md:flex flex-1 overflow-hidden max-w-6xl w-full mx-auto">
        {/* Sidebar */}
        <aside className="w-[360px] lg:w-[420px] xl:w-[480px] bg-white border-r border-gray-100 flex flex-col overflow-hidden shrink-0">
          {ideaList}
        </aside>

        {/* Main panel */}
        <main className="flex-1 bg-white overflow-hidden ml-px">
          {tab === "settings" ? settingsPanel : refinePanel}
        </main>
      </div>

      {/* Mobile: single-column with tabs */}
      <main className="md:hidden flex-1 overflow-hidden bg-white">
        {tab === "ideas" && ideaList}
        {tab === "refine" && refinePanel}
        {tab === "settings" && settingsPanel}
      </main>

      {/* Tab bar — mobile only */}
      <nav className="md:hidden flex border-t border-gray-100 shrink-0">
        {[
          { id: "ideas" as Tab, label: "Ideas" },
          { id: "refine" as Tab, label: "Refine" },
          { id: "settings" as Tab, label: "Settings" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 py-3 text-xs font-mono transition-colors ${
              tab === t.id
                ? "text-gray-900 border-t-2 border-gray-900 -mt-px"
                : "text-gray-400 hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
